import type { SummonDef } from '../../data/schema';
import { applyDamage, healPlayer, hitTarget, playerDamagePct, scaleDamage } from '../combat';
import { DEFAULT_BODY_HEIGHT_FACTOR } from '../config';
import type { Monster, Player, Summon, Vec2 } from '../types';
import { clampToWalkable, entitiesOfKind, getPlayer, spawn, type World } from '../world';

/** Tolérance sur les comparaisons de dates (le temps du monde s'accumule en flottants). */
const EPS = 1e-9;
/** Distance (px) à laquelle la bloqueuse et la folle se tiennent du joueur. */
const ESCORT_DIST = 60;
/** Marge (px) de contact avant qu'une gonflable explose. */
const EXPLODE_MARGIN = 4;
/** Tolérance (px) d'arrêt de la bloqueuse sur son point de garde. */
const GUARD_TOLERANCE = 4;

const dateReached = (world: World, t: number): boolean => world.time + EPS >= t;

/** Monstre vivant le plus proche (à distance égale, la plus petite id), ou null. */
function nearestMonster(world: World, from: Vec2): Monster | null {
  let best: Monster | null = null;
  let bestDist = Infinity;
  for (const m of entitiesOfKind<Monster>(world, 'monster')) {
    if (!m.alive) continue;
    const d = Math.hypot(m.pos.x - from.x, m.pos.y - from.y);
    if (d < bestDist) { best = m; bestDist = d; }
  }
  return best;
}

/** Fait mourir une invocation (animation et événement), sans autre effet. */
function killSummon(world: World, s: Summon): void {
  s.alive = false;
  s.anim = 'die';
  s.animTime = 0;
  s.vel = { x: 0, y: 0 };
  world.events.push({ type: 'death', entityId: s.id, defId: s.defId, pos: { ...s.pos }, by: 'player' });
}

/**
 * Crée une invocation du joueur. PV et durée sont majorés par `summonPct` ; au-delà de `maxActive` vivantes
 * du même type, la plus ancienne (`born` min, puis `id` min) disparaît sans exploser.
 * `damagePct` : bonus de dégâts du sort invocateur, gardé pour l'explosion.
 */
export function spawnSummon(world: World, def: SummonDef, at: Vec2, owner: Player, damagePct = 0): Summon {
  const grow = 1 + owner.summonPct / 100;
  for (;;) {
    let oldest: Summon | null = null;
    let alive = 0;
    for (const s of entitiesOfKind<Summon>(world, 'summon')) {
      if (!s.alive || s.defId !== def.id) continue;
      alive++;
      if (!oldest || s.born < oldest.born || (s.born === oldest.born && s.id < oldest.id)) oldest = s;
    }
    if (!oldest || alive < def.maxActive) break;
    oldest.exploded = true;
    applyDamage(world, oldest, oldest.hp, 'neutre', oldest.pos);
  }
  const hp = Math.round(def.hp * grow);
  return spawn<Summon>(world, {
    kind: 'summon', team: 'player', defId: def.id, sprite: def.sprite,
    pos: clampToWalkable(at, def.radius, world.room), vel: { x: 0, y: 0 }, radius: def.radius,
    height: def.height ?? def.radius * DEFAULT_BODY_HEIGHT_FACTOR, facing: { ...owner.facing },
    hp, hpMax: hp, alive: true, anim: 'idle', animTime: 0,
    speed: def.speed, behavior: def.behavior, until: world.time + def.lifetime * grow, born: world.time, nextAction: world.time, exploded: false, attackCooldown: 0, damagePct,
  });
}

/**
 * Cible d'un monstre : la plus proche entre le joueur et les invocations vivantes qui raillent (`taunt`)
 * et dont le centre est dans le rayon de raillerie. À égalité, le joueur. Le charger (boss) ne vise jamais que le joueur.
 */
export function monsterTarget(world: World, m: Monster, p: Player): Player | Summon {
  if (m.behavior === 'charger') return p;
  let best: Player | Summon = p;
  let bestDist = Math.hypot(p.pos.x - m.pos.x, p.pos.y - m.pos.y);
  for (const s of entitiesOfKind<Summon>(world, 'summon')) {
    if (!s.alive) continue;
    const taunt = world.content.summons[s.defId]?.taunt;
    if (!taunt) continue;
    const d = Math.hypot(s.pos.x - m.pos.x, s.pos.y - m.pos.y);
    if (d > taunt.radius || d >= bestDist) continue;
    best = s;
    bestDist = d;
  }
  return best;
}

/** Explosion : dégâts du joueur (bonus de dégâts du sort invocateur et actuels, pas summonPct) sur les monstres dans le rayon, puis mort de l'invocation si elle vit encore. */
function explodeSummon(world: World, p: Player, s: Summon): void {
  const ex = world.content.summons[s.defId]?.explode;
  s.exploded = true;
  if (ex) {
    const dmg = scaleDamage(ex.damage, s.damagePct + playerDamagePct(world, p));
    for (const mon of entitiesOfKind<Monster>(world, 'monster')) {
      if (!mon.alive || Math.hypot(mon.pos.x - s.pos.x, mon.pos.y - s.pos.y) > ex.radius + mon.radius) continue;
      hitTarget(world, p, mon, dmg, { lifestealPct: 0, slow: null, poison: ex.poison ?? null }, mon.pos);
    }
    world.events.push({ type: 'zoneHit', pos: { ...s.pos }, radius: ex.radius, element: ex.damage.element });
  }
  if (s.alive) killSummon(world, s);
}

/** Avance en ligne droite vers `target` en s'arrêtant à `stop` px de lui (glissement le long des obstacles). */
function moveToward(world: World, s: Summon, target: Vec2, stop: number, dt: number): void {
  const dx = target.x - s.pos.x, dy = target.y - s.pos.y;
  const d = Math.hypot(dx, dy);
  if (d <= stop || d === 0 || s.speed <= 0) {
    s.vel = { x: 0, y: 0 };
    if (s.anim === 'run') s.anim = 'idle';
    return;
  }
  const ux = dx / d, uy = dy / d;
  const step = Math.min(s.speed * dt, d - stop);
  s.facing = { x: ux, y: uy };
  s.vel = { x: ux * s.speed, y: uy * s.speed };
  s.pos = clampToWalkable({ x: s.pos.x + ux * step, y: s.pos.y + uy * step }, s.radius, world.room, s.pos);
  if (s.anim === 'idle') s.anim = 'run';
}

function stepBlocker(world: World, s: Summon, p: Player, dt: number): void {
  const m = nearestMonster(world, p.pos);
  let dir = p.facing;
  if (m) {
    const dx = m.pos.x - p.pos.x, dy = m.pos.y - p.pos.y, d = Math.hypot(dx, dy);
    if (d >= 1) dir = { x: dx / d, y: dy / d };
  }
  moveToward(world, s, { x: p.pos.x + dir.x * ESCORT_DIST, y: p.pos.y + dir.y * ESCORT_DIST }, GUARD_TOLERANCE, dt);
}

function stepExploder(world: World, p: Player, s: Summon, dt: number): void {
  const m = nearestMonster(world, s.pos);
  if (!m) { moveToward(world, s, s.pos, 0, dt); return; }
  if (Math.hypot(m.pos.x - s.pos.x, m.pos.y - s.pos.y) <= s.radius + m.radius + EXPLODE_MARGIN) { explodeSummon(world, p, s); return; }
  moveToward(world, s, m.pos, s.radius + m.radius, dt);
}

function stepHealer(world: World, def: SummonDef, s: Summon, p: Player, dt: number): void {
  moveToward(world, s, p.pos, ESCORT_DIST, dt);
  const heal = def.heal;
  if (!heal || !dateReached(world, s.nextAction)) return;
  if (Math.hypot(p.pos.x - s.pos.x, p.pos.y - s.pos.y) <= heal.radius) healPlayer(world, p, heal.amount, 'summon');
  s.nextAction += heal.interval;
}

function stepLure(world: World, def: SummonDef, s: Summon, dt: number): void {
  const m = nearestMonster(world, s.pos);
  if (m) moveToward(world, s, m.pos, s.radius + m.radius, dt);
  else moveToward(world, s, s.pos, 0, dt);
  const pulse = def.pulse;
  if (!pulse || !dateReached(world, s.nextAction)) return;
  for (const mon of entitiesOfKind<Monster>(world, 'monster')) {
    if (!mon.alive) continue;
    const dx = s.pos.x - mon.pos.x, dy = s.pos.y - mon.pos.y, d = Math.hypot(dx, dy);
    if (d > pulse.radius + mon.radius) continue;
    const step = Math.min(pulse.distance, d - (mon.radius + s.radius));
    if (step > 0) mon.pos = clampToWalkable({ x: mon.pos.x + (dx / d) * step, y: mon.pos.y + (dy / d) * step }, mon.radius, world.room);
  }
  world.events.push({ type: 'pull', pos: { ...s.pos }, radius: pulse.radius });
  s.nextAction += pulse.interval;
}

/**
 * Comportement des invocations du joueur, un pas de `dt`.
 * Une invocation explosive tuée par un coup (`!alive && !exploded`) explose ici, au début du pas suivant :
 * un pas de retard, déterministe, qui évite tout lien entre combat.ts et les invocations.
 */
export function summonSystem(world: World, dt: number): void {
  const p = getPlayer(world);
  if (!p) return;
  const summons = entitiesOfKind<Summon>(world, 'summon');
  for (const s of summons) {
    if (s.alive || s.exploded) continue;
    const def = world.content.summons[s.defId];
    if (def?.explode && (s.behavior === 'exploder' || s.behavior === 'lure')) explodeSummon(world, p, s);
  }
  for (const s of summons) {
    if (!s.alive) continue;
    const def = world.content.summons[s.defId];
    if (!def) continue;
    if (dateReached(world, s.until)) {
      if (def.explode && (s.behavior === 'exploder' || s.behavior === 'lure')) explodeSummon(world, p, s);
      else killSummon(world, s);
      continue;
    }
    if (s.behavior === 'blocker') stepBlocker(world, s, p, dt);
    else if (s.behavior === 'exploder') stepExploder(world, p, s, dt);
    else if (s.behavior === 'healer') stepHealer(world, def, s, p, dt);
    else stepLure(world, def, s, dt);
  }
}
